import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import {Button} from '../../../../../accountManagement/shared/Button'
import {Input} from '../../../../../accountManagement/shared/Input'
import {AddOnsDatePickerModal} from '../../../../../components/add-on-components/addons-datepicker-modal'
import {EaidValidation} from '../../../../../components/catalog-components/eaid-validation'
import {AddToOrderRow} from '../ProductPlanSelector.styles'
import {selectProposalInfo} from '../../../../../app-store/ProposalSlice'
import {selectEffectiveRole} from '../../../../../app-store/selector'
import {isPartnerChannelRole} from '../../../utils/isPartnerChannelRole'
import {MC_2_0_PLANS} from '../../../../../utils/constants'
import {
  ALLOWED_DOMAINS,
  FORBIDDEN_DOMAINS,
} from '../../../../../utils/constants'
import {DOMAIN_REGEX, getFieldsFromPlan} from '../planFieldsConfig'

const TL_ADDON_PLAN_CODES = new Set(['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m'])

export function isTLAddonPlan(code) {
  return TL_ADDON_PLAN_CODES.has(code)
}

// Reads a named key from both the object format {key: value} written by this
// panel and the [{name, value}] array format returned by mapCustomFields when
// the item comes from an existing Recurly subscription.
function readCfg(configuration, key) {
  if (Array.isArray(configuration)) {
    return configuration.find(f => f.name === key)?.value ?? ''
  }
  return configuration?.[key] ?? ''
}

export function TLAddonPanel({
  plan,
  selectedCountry,
  selectedProducts,
  itemInOrder,
  groupPlanCodes,
  groupId,
  validExtensionList,
  onAddProduct,
  onUpdateProduct,
  onClose,
}) {
  const client = useAuthClient()
  const thryvId = useSelector(selectProposalInfo)?.thryvId ?? ''
  // Partner flows (KeapPartner, PartnerOps, InfluencerOps) don't collect an EAID.
  const isPartnerChannel = isPartnerChannelRole(
    useSelector(selectEffectiveRole),
  )
  // Catalog cart items use configuredOptions; existing subscription items can
  // still provide legacy configuration. Supporting both lets every entry
  // point (proposal tool, new order, and upgrade) rehydrate this panel.
  const cfg = itemInOrder?.configuredOptions ?? itemInOrder?.configuration ?? {}
  const isAlreadyOnAccount = Boolean(itemInOrder?.alreadyIncluded)

  // MC 2.0 domain carry-over: when MC 2.0 is in the cart, Thryv Leads
  // requires a mandatory domain and auto-populates from the MC plan's domain.
  // Mirrors options.js:1218-1250's isMC20InCart / tlDomainConfig logic.
  const mc20Item = (selectedProducts ?? []).find(p =>
    MC_2_0_PLANS.includes(p.planCode),
  )
  const isMC20InCart = Boolean(mc20Item)
  const mcDomain = readCfg(mc20Item?.configuredOptions, 'domain')

  const domainFieldKey =
    getFieldsFromPlan(plan).find(f => f.validators?.includes('domain'))?.key ??
    'tlDomain'

  const [leadsId, setLeadsId] = useState(readCfg(cfg, 'thryvLeadsId'))
  const hasSavedLeadsId = Boolean(readCfg(cfg, 'thryvLeadsId'))
  const [validation, setValidation] = useState(
    isAlreadyOnAccount || hasSavedLeadsId
      ? {passed: true, budget: itemInOrder.quantity}
      : null,
  )
  const [billingDate, setBillingDate] = useState(
    readCfg(cfg, 'billingDate') || null,
  )
  const [cohortEaid, setCohortEaid] = useState(readCfg(cfg, 'cohortEaid'))
  const [isEaidFound, setIsEaidFound] = useState(
    Boolean(readCfg(cfg, 'cohortEaid')),
  )
  const [allowInvoice, setAllowInvoice] = useState(false)
  const [isCohort, setIsCohort] = useState(false)
  const [status, setStatus] = useState(hasSavedLeadsId ? 'valid' : 'idle') // 'idle' | 'loading' | 'valid' | 'invalid'

  // tlDomain: pre-fill from an existing item's saved config, or auto-populated
  // from mcDomain below via useEffect.
  const [tlDomain, setTlDomain] = useState(readCfg(cfg, domainFieldKey))
  const [tlDomainTouched, setTlDomainTouched] = useState(false)

  // Auto-populate domain from MC 2.0 plan's domain when not yet set.
  // Mirrors options.js:1242-1251.
  useEffect(() => {
    if (isMC20InCart && mcDomain && !tlDomain) {
      setTlDomain(mcDomain)
    }
    // Only run when MC 2.0 presence or the MC domain changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMC20InCart, mcDomain])

  const isDomainValid = (() => {
    const trimmed = String(tlDomain).trim().toLowerCase()
    if (!DOMAIN_REGEX.test(trimmed)) return false
    const normalized = trimmed
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split(/[/?#]/)[0]
    const isForbidden = FORBIDDEN_DOMAINS.some(
      d =>
        normalized === `${d}.com` ||
        normalized.includes(`.${d}.`) ||
        normalized.startsWith(`${d}.`) ||
        normalized.endsWith(`.${d}.com`),
    )
    if (isForbidden) return false
    if (ALLOWED_DOMAINS.some(d => trimmed.includes(d))) return true
    if (validExtensionList?.length) {
      const tld = normalized.split('.').pop()
      if (!validExtensionList.includes(tld)) return false
    }
    return true
  })()

  const handleValidate = async () => {
    if (!leadsId || isAlreadyOnAccount) return
    setStatus('loading')
    try {
      const result = await client(
        `plans/validate-thryv-leads/${encodeURIComponent(leadsId)}`,
      )
      if (result?.passed && result?.budget != null) {
        setValidation(result)
        setStatus('valid')
      } else {
        setValidation(null)
        setStatus('invalid')
      }
    } catch {
      setValidation(null)
      setStatus('invalid')
    }
  }

  // Domain is mandatory when MC 2.0 is in the cart. Mirrors
  // options.js:1317-1318's isContinueDisabledForTLDomain.
  const canAdd =
    Boolean(validation?.passed) &&
    !isAlreadyOnAccount &&
    (!isMC20InCart || isDomainValid)

  const handleAddToOrder = () => {
    if (!canAdd) {
      if (isMC20InCart) setTlDomainTouched(true)
      return
    }

    const configuredOptions = {
      thryvLeadsId: leadsId,
      ...(billingDate && {billingDate}),
      ...(cohortEaid && {cohortEaid}),
      ...(isMC20InCart && tlDomain && {[domainFieldKey]: tlDomain}),
    }

    const product = {
      productId: plan.code,
      name: plan.name,
      // Use the validated budget as the displayed/billed dollar amount,
      // mirroring options.js: amount = thryvLeadsValidation.budget.
      price: validation.budget,
      type: 'addon',
      billingFrequency:
        plan.productBillingType === 'Annual' ? 'annual' : 'monthly',
      quantity: validation.budget ?? 1,
      configuredOptions,
      excludes: plan.excludes ?? [],
      autoAddCenters: plan.autoAddCenters ?? [],
      disableApps: plan.disableApps ?? [],
      disableCenters: plan.disableCenters ?? [],
      maxQty: plan.maxQty ?? null,
      parentProduct: plan.parentProduct,
    }

    if (itemInOrder && onUpdateProduct) {
      onUpdateProduct(product)
    } else {
      onAddProduct(product, groupPlanCodes, groupId)
    }
    onClose()
  }

  console.log('allowInvoice : ', allowInvoice)
  console.log('isCohort : ', isCohort)

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
      <Input
        label="Leads ID"
        placeholder="Enter Leads ID"
        type="text"
        value={leadsId}
        disabled={isAlreadyOnAccount}
        errorText={status === 'invalid' ? 'Invalid Leads ID' : undefined}
        helperText={status === 'valid' ? 'Validated' : undefined}
        onChange={e => {
          setLeadsId(e.target.value)
          setValidation(null)
          setStatus('idle')
        }}
        rightAction={
          <Button
            type="button"
            variant="secondary-field-blue"
            size="xl"
            isLoading={status === 'loading'}
            disabled={isAlreadyOnAccount || status === 'loading' || !leadsId}
            onClick={handleValidate}
          >
            {status === 'loading' ? 'Validating…' : 'Validate'}
          </Button>
        }
      />

      {isMC20InCart && (
        <Input
          label="Domain"
          placeholder="e.g. yourdomain.com"
          type="text"
          value={tlDomain}
          errorText={
            tlDomainTouched && !isDomainValid
              ? 'Please enter a valid domain (e.g. yourdomain.com)'
              : undefined
          }
          onChange={e => {
            setTlDomain(e.target.value)
            setTlDomainTouched(true)
          }}
        />
      )}

      {selectedCountry === 'US' && !isPartnerChannel && (
        <EaidValidation
          cohortEaid={cohortEaid}
          isEaidFound={isEaidFound}
          isOptional
          setCohortEaid={setCohortEaid}
          setIsEaidFound={setIsEaidFound}
          setAllowInvoice={setAllowInvoice}
          setIsCohort={setIsCohort}
          thryvId={thryvId}
          variant="proposal"
        />
      )}

      {validation?.passed && (
        <div>
          <div className="row ml-3 mb-4">
            Do you want to start the billing on a different date?
          </div>
          <AddOnsDatePickerModal
            setSelectedDate={setBillingDate}
            billingDate={billingDate}
            maxDate={30}
          />
        </div>
      )}

      <AddToOrderRow>
        <Button
          type="button"
          variant="action-orange"
          size="sm"
          disabled={!canAdd}
          onClick={handleAddToOrder}
        >
          {itemInOrder && !isAlreadyOnAccount ? 'Update Order' : 'Add To Order'}
        </Button>
      </AddToOrderRow>
    </div>
  )
}
